import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FetchError } from "../../../types";
import { configuration } from "../../../../configuration";
import { useAccessToken } from "../../../use-access-token";
import { queryKeys } from "../../queryKeys";
import { useSignalR } from "../../../../hooks";

type Variables = {
  agendaStepGuid: string;
};

const useLockAgendaItemTextContentRequest = () => {
  const { getAccessToken } = useAccessToken();
  const queryClient = useQueryClient();

  async function lockAgendaItemTextContentRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();

    const response = await fetch(
      `${configuration.apiRootUrl}/agenda-items/${variables.agendaStepGuid}/text-content/lock`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        method: "put",
      }
    );

    if (response.status === 403) {
      // Someone else has locked the content, refetch to update the UI to reflect the current state
      queryClient.refetchQueries(queryKeys.meetings.getAgendaItemTextContent(variables.agendaStepGuid));
    } else if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { lockAgendaItemTextContentRequest };
};

const useLockAgendaItemTextContent = () => {
  const { lockAgendaItemTextContentRequest } = useLockAgendaItemTextContentRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: lockAgendaItemTextContentRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchAgendaItemContent(variables.agendaStepGuid);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.meetings.getAgendaItemTextContent(variables.agendaStepGuid));
    },
  });
};

export { useLockAgendaItemTextContent };
